import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { audioBlobToAttachment } from "@/pages/multi-agent/ide/attachments";
import { VoiceRecorder, micErrorMessage, noAudioMessage } from "@/pages/multi-agent/ide/voiceRecorder";
import type { MultiAgentAttachment } from "@/services/multi-agent/api";

const MAX_REC_SECONDS = 55;
// If no audio has arrived by this point, the input is almost certainly a dead
// Bluetooth HFP track (or a muted mic). Warn once so the user isn't left
// recording silence for the full 55s. Kept generous so slow-to-warm-up
// Bluetooth links (HFP handshake) aren't flagged prematurely.
const NO_AUDIO_WARN_SECONDS = 4;

export interface UseVoiceRecorderReturn {
  recording: boolean;
  recSeconds: number;
  recLevel: number;
  toggleMic: () => Promise<void>;
  cancelMic: () => void;
  onMicPointerDown: (e: React.PointerEvent) => void;
  onMicPointerUp: () => void;
  fmtRecTime: (s: number) => string;
}

/**
 * Manages the full voice-recording lifecycle: permission prompt, recording,
 * live level meter, mm:ss timer, auto-stop at ~55s, and transcription handoff.
 *
 * `onTranscribed` receives the resolved attachment (audio → text) after stop.
 * The caller is responsible for adding it to the attachment list.
 */
export function useVoiceRecorder(
  onTranscribed: (att: MultiAgentAttachment) => Promise<void>,
): UseVoiceRecorderReturn {
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [recLevel, setRecLevel] = useState(0);

  const recorderRef = useRef<VoiceRecorder | null>(null);
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recLevelRafRef = useRef<number | null>(null);
  const heldMicRef = useRef(false);
  const suppressMicClickRef = useRef(false);
  const noAudioWarnedRef = useRef(false);

  const stopRecTimer = () => {
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
    if (recLevelRafRef.current != null) { cancelAnimationFrame(recLevelRafRef.current); recLevelRafRef.current = null; }
    setRecSeconds(0);
    setRecLevel(0);
  };

  const stopAndTranscribe = async () => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    setRecording(false);
    stopRecTimer();
    if (!rec) return;
    try {
      const result = await rec.stop();
      if ("noAudio" in result) { toast(noAudioMessage(result.noAudio)); return; }
      const att = await audioBlobToAttachment(result);
      if ("error" in att) { toast.error(att.error); return; }
      await onTranscribed(att);
    } catch {
      toast.error("Could not process the recording");
    }
  };

  const cancelMic = () => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    setRecording(false);
    stopRecTimer();
    rec?.cancel();
  };

  const startMic = async () => {
    if (recorderRef.current) return;
    try {
      const rec = new VoiceRecorder();
      noAudioWarnedRef.current = false;
      await rec.start({
        // A Bluetooth headset connecting/disconnecting mid-take swaps the OS
        // default input; the current stream may then go silent (HFP). Warn so
        // the user re-records on the right device rather than capturing silence.
        onDeviceChange: () => {
          if (recorderRef.current !== rec) return;
          toast("Audio device changed — stop and re-record to use the new mic.");
        },
      });
      recorderRef.current = rec;
      setRecording(true);
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => {
        // Live dead-stream detection: if a few seconds pass with zero audio
        // (classic Bluetooth HFP dead track / muted mic), tell the user now
        // instead of after a silent 55s take. Warns once per take.
        const active = recorderRef.current;
        setRecSeconds((s) => {
          const next = s + 1;
          if (active && !noAudioWarnedRef.current && next >= NO_AUDIO_WARN_SECONDS
              && !active.hasCapturedAudio) {
            noAudioWarnedRef.current = true;
            toast(noAudioMessage(active.bluetooth ? "silent-bluetooth" : "silent"));
          }
          if (next >= MAX_REC_SECONDS) {
            toast("Reached the recording limit — transcribing.");
            void stopAndTranscribe();
          }
          return next;
        });
      }, 1000);
      const tick = () => {
        const r = recorderRef.current;
        if (!r) return;
        setRecLevel(r.getLevel());
        recLevelRafRef.current = requestAnimationFrame(tick);
      };
      recLevelRafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      toast.error(micErrorMessage(err));
    }
  };

  const toggleMic = async () => {
    if (suppressMicClickRef.current) { suppressMicClickRef.current = false; return; }
    if (recording) { await stopAndTranscribe(); return; }
    await startMic();
  };

  const onMicPointerDown = (e: React.PointerEvent) => {
    if (recording) return;
    e.preventDefault();
    heldMicRef.current = true;
    void startMic();
  };

  const onMicPointerUp = () => {
    if (!heldMicRef.current) return;
    heldMicRef.current = false;
    suppressMicClickRef.current = true;
    if (recorderRef.current) void stopAndTranscribe();
  };

  const fmtRecTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  useEffect(() => () => {
    if (recTimerRef.current) clearInterval(recTimerRef.current);
    if (recLevelRafRef.current != null) cancelAnimationFrame(recLevelRafRef.current);
    recorderRef.current?.cancel();
  }, []);

  return { recording, recSeconds, recLevel, toggleMic, cancelMic, onMicPointerDown, onMicPointerUp, fmtRecTime };
}
